<template>
  <PForm
    ref="pForm"
    :heading="`${formState.edit ? 'Edit' : 'Request for'} Debit Note`"
    :primary-cta="primaryCta"
    @close="onClose"
    @reset="onReset"
    @submit.prevent="onSubmit"
  >
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-12">
          <div class="border-none q-ma-none q-pa-none">
            <PField v-model="formData.dn_for" label="Debit note against?" dense>
              <template #control>
                <POptionGroup
                  v-model="formData.dn_for"
                  :options="[
                    {
                      description:
                        '(Default) DN to Vendor against a Purchase Bill',
                      label: 'Purchase Bill',
                      value: 'bill',
                    },
                    {
                      description: 'DN to Customer against an Invoice',
                      label: 'Invoice',
                      value: 'invoice',
                    },
                  ]"
                  @update:model-value="onUpdateDnFor"
                />
              </template>
            </PField>
          </div>
        </div>
      </div>
    </div>
    <q-separator spaced="xl" />
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-4">
          <PSelectInput
            v-if="formData.dn_for === 'bill'"
            v-model="formData.bill_id"
            :options="unref(purchaseBillFilter.entities)"
            input-class="ellipsis"
            label="Purchase Bill"
            option-label="number"
            option-value="_id"
            placeholder="Select purchase bill"
            :no-options-text="NO_OPTIONS_TEXT.bills"
            required
            @filter="purchaseBillFilter.onFilter"
            @filter-abort="purchaseBillFilter.onFilterAbort"
            @update:model-value="onUpdatePurchaseBill"
          />
          <PSelectInput
            v-else
            v-model="formData.invc_id"
            :no-options-text="NO_OPTIONS_TEXT.invoices"
            :options="
              unref(invoiceFilter.entities)?.filter((invoice) => {
                return invoice.status_cd !== 'cancelled' && invoice.number;
              })
            "
            :validations="{
              label: 'Invoice',
              required: true,
            }"
            input-class="ellipsis"
            label="Select an Invoice"
            option-label="number"
            option-value="_id"
            placeholder="Select invoice"
            @filter="invoiceFilter.onFilter"
            @filter-abort="invoiceFilter.onFilterAbort"
            @update:model-value="onUpdateInvoice"
          />
        </div>
        <div
          v-if="formData.dn_for === 'bill' && formData.bill_id"
          class="col-8"
        >
          <PSelectInput
            v-model="formData.reference_invcs"
            :emit-value="false"
            :map-options="false"
            :options="referenceInvoices"
            input-class="ellipsis"
            label="Ref. Invoices"
            option-label="number"
            option-value="_id"
            placeholder="Select invoices"
            multiple
            use-chips
            @filter="invoiceFilter.onFilter"
            @filter-abort="invoiceFilter.onFilterAbort"
          >
            <template #no-option>
              <q-item class="bg-primary-20 text-neutral-80">
                <q-item-section>
                  {{ NO_OPTIONS_TEXT.refInvoices }}
                </q-item-section>
              </q-item>
            </template>
          </PSelectInput>
        </div>
        <div v-else class="col-8"></div>
        <div class="col-3">
          <PInput
            v-if="formData.dn_for === 'bill'"
            v-model.trim="formData.bill_number"
            label="Bill No."
            placeholder="Enter value"
            required
            disable
          />
          <PInput
            v-else
            v-model.trim="formData.invc_number"
            label="Invoice No."
            placeholder="Enter value"
            required
            disable
          />
        </div>
        <div class="col-3">
          <PVDateInput
            v-if="formData.dn_for === 'bill'"
            v-model="formData.bill_date"
            label="Bill Date"
            placeholder="DD-MM-YYYY"
            required
            :disable="true"
          />
          <PVDateInput
            v-else
            v-model="formData.invc_date"
            label="Invoice Date"
            placeholder="DD-MM-YYYY"
            required
            :disable="true"
          />
        </div>
        <div class="col-3">
          <PVDateInput
            v-model="formData.date"
            label="DN Date"
            placeholder="DD-MM-YYYY"
            required
            :disable="true"
          />
        </div>
        <div class="col-3">
          <PInput
            v-model.trim="formData.vehicle_number"
            label="Vehicle No."
            placeholder="Enter value"
            required
            disable
          />
        </div>
        <p v-if="areActiveDNsAgainstPB" class="text-warning-70">
          There are active DNs against the select purchase bill. Please make
          sure you are not requesting duplicates. Filter with PB no.in DN tab to
          find out.
        </p>
        <div class="col-6 column q-col-gutter-xs">
          <PSelectInput
            v-if="formData.dn_for === 'bill'"
            v-model="unref(formData).billing_address"
            :options="unref(billingAddressFilter.entities)"
            :disable="!unref(formData).bill_id"
            :options-loading="unref(billingAddressFilter.loading)"
            :option-label="
              (val) =>
                getAddressOptionLabel(
                  val,
                  unref(selectedPurchaseBill)?.seller?.gstin,
                )
            "
            label="Bill To"
            required
            placeholder="Select an address"
            @virtual-scroll="billingAddressFilter.onScroll"
            @filter="billingAddressFilter.onFilter"
            @filter-abort="billingAddressFilter.onFilterAbort"
            @update:model-value="
              (value) =>
                updateAddress(
                  value,
                  'billing_address',
                  unref(selectedPurchaseBill)?.seller?.gstin,
                )
            "
          >
            <template #option="{ itemProps, opt }">
              <AddressOption
                :selected="
                  opt._id === unref(formData).billing_address_info?._id
                "
                :passed-props="itemProps"
                :address="opt"
                :gstin="unref(selectedPurchaseBill)?.seller?.gstin"
              />
            </template>
          </PSelectInput>
          <PInput
            v-model.trim="temp_billing_address"
            :label="formData.dn_for === 'invoice' ? 'Bill To' : ''"
            :disable="true"
            class="col"
            placeholder="Enter billing address"
            type="textarea"
            autogrow
            required
          />
        </div>
        <div class="col-6 column q-col-gutter-xs">
          <PSelectInput
            v-if="formData.dn_for === 'bill'"
            v-model="unref(formData).shipping_address"
            :disable="!unref(formData).bill_id"
            :options="unref(shippingAddressFilter.entities)"
            :options-loading="unref(shippingAddressFilter.loading)"
            :option-label="
              (val) =>
                getAddressOptionLabel(
                  val,
                  unref(selectedPurchaseBill).seller?.gstin,
                )
            "
            label="Ship To"
            required
            placeholder="Select an address"
            @virtual-scroll="shippingAddressFilter.onScroll"
            @filter="shippingAddressFilter.onFilter"
            @filter-abort="shippingAddressFilter.onFilterAbort"
            @update:model-value="
              (value) =>
                updateAddress(
                  value,
                  'shipping_address',
                  unref(selectedPurchaseBill)?.seller?.gstin,
                )
            "
          >
            <template #option="{ itemProps, opt }">
              <AddressOption
                :selected="
                  opt._id === unref(formData).shipping_address_info?._id
                "
                :passed-props="itemProps"
                :address="opt"
                :gstin="unref(selectedPurchaseBill).seller?.gstin"
              />
            </template>
          </PSelectInput>
          <PInput
            v-model.trim="temp_shipping_address"
            :label="formData.dn_for === 'invoice' ? 'Ship To' : ''"
            :disable="true"
            class="col"
            placeholder="Enter shipping address"
            type="textarea"
            autogrow
            required
          />
        </div>
      </div>
    </div>
    <q-separator class="q-mb-xl q-mt-sm" />
    <div class="border-none q-ma-none q-pa-none">
      <PField v-model="formData.type_cd" label="Choose debit note type">
        <template #control>
          <POptionGroup
            v-model="formData.type_cd"
            :options="unref(dnTypeOptions)"
            @update:model-value="onUpdateTypeCode"
          />
        </template>
      </PField>
    </div>
    <div
      v-if="formData.invc_id || formData.bill_id"
      class="overflow-hidden q-mt-lg"
    >
      <legend class="q-mb-sm text-neutral text-weight-medium">Items</legend>

      <q-separator v-if="formData?.lineitems?.length" />

      <PTable
        v-if="formData.lineitems.length"
        :pagination="pagination"
        rows-per-page-label=" "
        :rows-per-page-options="[]"
        :columns="productListColumns"
        :rows="formData.lineitems"
        :style="{ margin: '0 -15px', padding: 0 }"
        table-header-class="text-neutral-70 bg-neutral-10"
        title-class="text-neutral text-md"
        dense
        flat
        wrap-cells
      >
        <template #body-cell="props">
          <q-td :key="props.col.name" :props="props">
            <PEditable
              :model-value="props.value"
              :type="props.col.type"
              disable
            />
          </q-td>
        </template>

        <template #body-cell-name="props">
          <q-td :key="props.col.name" :props="props">
            <ItemNameCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-rate="props">
          <q-td :key="props.col.name" :props="props">
            <ItemRateCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-amount="props">
          <q-td :key="props.col.name" :props="props">
            <ItemAmountCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-qty="props">
          <q-td :key="props.col.name" :props="props">
            <ItemQuantityCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-action="props">
          <q-td :props="props">
            <div class="flex no-wrap justify-center">
              <PBtn
                color="primary"
                dense
                flat
                fluid
                rounded
                title="Edit Item"
                @click="
                  editItem({
                    item: props.row,
                    index: props.rowIndex,
                    entityType: 'Debit Note',
                  })
                "
              >
                <EditIcon class="icon--sm" />
              </PBtn>
              <PBtn dense flat rounded title="Delete Item">
                <RemoveIcon
                  class="icon--sm"
                  @click="removeProduct(props.rowIndex)"
                />
              </PBtn>
            </div>
          </q-td>
        </template>
      </PTable>
      <p v-else class="q-my-xl text-neutral-50">
        <span class="block q-mb-xs">No Items</span>
        <span class="text-error-60">
          <q-icon class="icon--sm" name="sym_r_error_outline" />
          At least one item is required.
        </span>
      </p>
      <div class="row">
        <PBtn
          class="q-mt-md q-px-xs"
          dense
          outline
          @click="addItem({ entityType: 'Debit Note', items: entityLineItems })"
        >
          <AddIcon class="icon--sm q-mr-sm" />
          <span>Add items to Debit Note</span>
        </PBtn>
      </div>
    </div>
    <q-separator spaced="xl" />
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-6">
          <PInput
            v-model.trim="formData.remarks"
            label="Remarks (if any)"
            placeholder="Enter remarks"
            type="textarea"
            autogrow
          />
        </div>
        <div v-if="formData.lineitems.length" class="col-6">
          <div class="row q-col-gutter-lg">
            <div class="col-6 ellipsis">
              <p class="q-mb-none">Total {{ formData.type }}</p>
              <span class="text-neutral-70 text-xs"> Excludes Tax </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount
                      ? getTotalAmountWithoutTax()
                      : 0,
                    style: "currency",
                  })
                }}
              </div>
            </div>
            <div class="col-6 ellipsis">
              <span class="text-neutral" :title="`Total ${formData.type} Tax`">
                Total {{ formData.type }} Tax
              </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_tax,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div
            v-if="formData.dn_for === 'bill' && formData.tds?.code"
            class="row q-mt-lg"
          >
            <div class="col-6 ellipsis">
              <TdsInfoTooltip :percentage="formData.tds?.percentage" />
            </div>
            <div class="col-6">
              <div class="text-right">
                -
                {{
                  numberFormat({
                    number: formData.total_amount ? formData.tds.amount : 0,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div class="row q-mt-lg">
            <div class="col-6 ellipsis">
              <p class="q-mb-none">Total {{ formData.type }}</p>
              <span class="text-neutral-70 text-xs"> Includes Tax </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <q-separator class="q-mb-xl q-mt-md" />
    <div class="q-mt-sm text-weight-bold">
      {{ FORM_INFO }}
    </div>
  </PForm>
</template>

<script setup>
import AddIcon from "assets/icons/add.svg";
import EditIcon from "assets/icons/edit.svg";
import RemoveIcon from "assets/icons/remove.svg";
import debitNoteRequestFormModel from "src/modules/purchase-order/models/debitNoteRequestForm";
import { useProductList } from "src/modules/purchase-order/composables/product-list";
import { numberFormat } from "assets/scripts/utilities/helpers";
import { FORM_INFO, NO_OPTIONS_TEXT } from "assets/scripts/utilities/constants";
import { index as getAddress } from "src/api/address";

const areActiveDNsAgainstPB = ref(false);

const onUpdateDnFor = (dn_for) => {
  resetForm();

  clearTempAddresses();

  unref(formData).dn_for = dn_for;

  areActiveDNsAgainstPB.value = false;

  selectedPurchaseBill.value = {};
};

const dnTypeOptions = computed(() => {
  const options = [
    {
      description: "For milk products",
      label: "Incentives",
      value: "incentives",
    },
    {
      description: "For milk buyers",
      label: "Sales Increase",
      value: "increase_sales",
    },
  ];

  if (unref(formData).dn_for === "bill") {
    unref(formData).type = "Returns";

    unref(formData).type_cd = "returns";

    options.unshift({
      description: "For returned / damaged items",
      label: "Returns",
      value: "returns",
    });

    options.push({
      description: "For service items",
      disable: true,
      label: "Services",
      value: "services",
    });
  } else {
    unref(formData).type = "Incentives";

    unref(formData).type_cd = "incentives";
  }

  return options;
});

const onUpdateTypeCode = (type_cd) => {
  const type = unref(dnTypeOptions).find(
    (option) => option.value === type_cd,
  ).label;

  unref(formData).type = type;

  resetFormLineItems();
};

let billingAddressFilter = {};
let shippingAddressFilter = {};

const updateAddress = (info, address_type, gstin) => {
  if (!info) {
    return;
  }

  unref(formData)[`${address_type}_info`] = {
    city: info.city,
    state: info.state,
    pincode: info.pincode,
    address_line: info.address_line,
    state_code: info.state_code,
    address_id: info._id,
    shop_name: unref(selectedPurchaseBill)?.seller.shop_name,
  };

  const gstinText = gstin ? `\nGSTIN: ${gstin}` : "";
  const concatenatedAddress =
    (info.address_line || "") +
    (info.city ? ", " + info.city : "") +
    (info.state ? ", " + info.state : "") +
    (info.pincode ? ", " + info.pincode : "") +
    (gstinText ? " " + gstinText : "");

  unref(formData)[`${address_type}`] = concatenatedAddress;

  if (address_type == "billing_address") {
    temp_billing_address.value =
      unref(selectedPurchaseBill)?.seller.shop_name +
      "\n" +
      concatenatedAddress;
  } else {
    temp_shipping_address.value =
      unref(selectedPurchaseBill)?.seller.shop_name +
      "\n" +
      concatenatedAddress;
  }
};

const getAddressOptionLabel = (address, gstin) => {
  if (!address) return;
  else if (typeof address === "string") {
    return address;
  } else if (typeof address === "object") {
    const addressLine = address.address_line || address.address;

    return (
      (addressLine || "") +
      (address.city ? ", " + address.city : "") +
      (address.state ? ", " + address.state : "") +
      (address.pincode ? ", " + address.pincode : "") +
      (gstin ? "\nGSTIN: " + gstin : "")
    );
  }
};

const resetFormLineItems = () => {
  unref(formData).lineitems = [];
};

const pForm = ref(null);

const { formState, debitNote } = storeToRefs(useDebitNoteStore());
const { purchaseOrder } = storeToRefs(usePurchaseOrderStore());

const { postDebitNote, putDebitNote, toggleDebitNoteRequestForm } =
  useDebitNoteStore();

const { formData, isLoading, closeForm, resetForm, submitForm } = useForm({
  formModel: debitNoteRequestFormModel(),
  entity: debitNote,
  formRef: pForm,
  isForm: formState,
  post: postDebitNote,
  put: putDebitNote,
  toggleForm: toggleDebitNoteRequestForm,
});

const populateFormData = async (entity) => {
  if (entity) {
    clearTempAddresses();

    const { vehicle_number } = entity;
    const { payment_terms } = unref(purchaseOrder);

    formData.value = {
      ...unref(formData),
      payment_terms,
      vehicle_number,
    };

    if (unref(formData).dn_for === "bill") {
      const { date, number, seller = {} } = entity;

      const addressResponse = await getAddress({
        params: {
          get_primary: true,
          user_id: seller?._id || seller,
        },
      });
      const sellerPrimaryAddress = addressResponse?.data?.data?.[0] || {};

      const address = `${sellerPrimaryAddress.address_line}, ${sellerPrimaryAddress.city}, ${sellerPrimaryAddress.state}, ${sellerPrimaryAddress.pincode}\nGSTIN: ${unref(selectedPurchaseBill)?.seller?.gstin}`;
      const address_info = {
        ...sellerPrimaryAddress,
        address_id: sellerPrimaryAddress._id,
        shop_name: seller.shop_name,
      };

      temp_billing_address.value =
        unref(selectedPurchaseBill)?.seller.shop_name + "\n" + address;

      temp_shipping_address.value =
        unref(selectedPurchaseBill)?.seller.shop_name + "\n" + address;

      formData.value = {
        ...unref(formData),
        bill_date: date,
        bill_id: entity._id,
        bill_number: number,
        po_id: unref(purchaseOrder)._id,
        seller: seller?._id || seller,
        tds: entity.tds,
        billing_address: address,
        billing_address_info: address_info,
        shipping_address: address,
        shipping_address_info: address_info,
      };
    } else {
      const {
        _id,
        date,
        number,
        po_id,
        po_info,
        billing_address: entity_billing_address,
        shipping_address: entity_shipping_address,
        billing_address_info: entity_billing_address_info,
        shipping_address_info: entity_shipping_address_info,
      } = entity;
      const { buyer_name, buyer_gstin } = po_info;

      let { buyer } = unref(purchaseOrder);
      const { shop_name } = buyer;

      temp_billing_address.value = `${shop_name}\n${entity_billing_address}`;

      temp_shipping_address.value = `${shop_name}\n${entity_shipping_address}`;

      formData.value = {
        ...unref(formData),
        billing_address: entity_billing_address,
        billing_address_info: {
          ...entity_billing_address_info,
          shop_name,
        },
        invc_date: date,
        invc_id: _id,
        invc_number: number,
        po_id,
        buyer,
        buyer_name,
        buyer_gstin,
        shipping_address: entity_shipping_address,
        shipping_address_info: {
          ...entity_shipping_address_info,
          shop_name,
        },
      };
    }
  }
};

const onSubmit = async () => {
  try {
    await submitForm(false);

    clearTempAddresses();

    closeForm();
  } catch (error) {
    console.error(error);
  }
};

let entityLineItems = ref([]);

const setEntityLineItems = (entity) => {
  entityLineItems.value = entity?.lineitems;

  resetFormLineItems();
};

let selectedPurchaseBill = ref({});
const temp_billing_address = ref("");
const temp_shipping_address = ref("");

const onUpdatePurchaseBill = () => {
  selectedPurchaseBill.value = unref(unref(purchaseBillFilter).entities).find(
    (bill) => bill._id === unref(formData).bill_id,
  );

  billingAddressFilter = useFilter({
    entity: "address",
    options: {
      params: {
        get_primary: true,
        type: "billing",
        user_id: unref(selectedPurchaseBill)?.seller?._id,
      },
    },
  });

  shippingAddressFilter = useFilter({
    entity: "address",
    options: {
      params: {
        get_primary: true,
        type: "shipping",
        user_id: unref(selectedPurchaseBill)?.seller?._id,
      },
    },
  });

  unref(formData).reference_invcs = [];

  populateFormData(unref(selectedPurchaseBill));

  setEntityLineItems(unref(selectedPurchaseBill));

  areActiveDNsAgainstPB.value = Boolean(
    unref(selectedPurchaseBill)?.debit_notes?.length > 0,
  );
};

const onUpdateInvoice = (invoice) => {
  invoice = unref(unref(invoiceFilter).entities).find(
    (invoice) => invoice._id === unref(formData).invc_id,
  );

  populateFormData(invoice);

  setEntityLineItems(invoice);
};

let purchaseBillFilter = ref({}),
  invoiceFilter = ref({});

watchEffect(() => {
  purchaseBillFilter.value = useFilter({
    entity: "purchaseBill",
    options: {
      params: {
        po_id: unref(purchaseOrder)._id,
        status_cd: "active",
      },
    },
  });
});

const { counts } = storeToRefs(usePurchaseOrderStore());

watchEffect(() => {
  invoiceFilter.value = useFilter({
    entity: "invoice",
    options: {
      params: {
        bill_id: unref(selectedPurchaseBill)?._id,
        po_id: unref(purchaseOrder)._id,
        status_cd: "active",
        stages: "pod_accepted,pod_received,completed",
      },
    },
    pageSize: unref(counts)?.invoices_count || 25,
  });
});

const referenceInvoices = computed(() => {
  return unref(unref(invoiceFilter).entities)?.filter((invoice) => {
    return (
      invoice.status_cd === "active" &&
      invoice.bill_id === unref(formData).bill_id &&
      ["completed", "pod_received", "pod_accepted"].includes(invoice.stage_cd)
    );
  });
});

const {
  pagination,
  addItem,
  editItem,
  removeProduct,
  getTotalAmountWithoutTax,
} = useProductList({
  formData,
});

const productListColumns = computed(() => {
  const columns = [
    {
      align: "left",
      name: "name",
      label: "Name",
      field: "name",
    },
    {
      align: "left",
      name: "qty",
      label: "Qty Returned",
      field: "qty",
    },
    {
      align: "right",
      name: "rate",
      label: "Rate",
      field: "rate",
      type: "currency",
    },
    {
      align: "right",
      name: "amount",
      label: "Total",
      field: "amount",
    },
    {
      align: "center",
      classes: "sticky--right",
      headerClasses: "sticky--right",
      name: "action",
      label: "Action",
    },
  ];

  return columns;
});

const clearTempAddresses = () => {
  temp_billing_address.value = "";

  temp_shipping_address.value = "";
};

const onClose = () => {
  clearTempAddresses();

  closeForm();
};

const onReset = () => {
  resetForm();

  clearTempAddresses();

  areActiveDNsAgainstPB.value = false;
};

const primaryCta = computed(() => {
  return {
    loading: unref(isLoading),
    disable: !unref(formData).lineitems.length,
  };
});
</script>
